import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface BlogPost {
    id: string;
    title: string;
    excerpt: string;
    slug: string;
    coverImage: string;
    author: {
        name: string;
        picture: string;
    };
    category: string;
    publishedAt: string;
}

const CATEGORIES = ['Tous', 'Tech', 'Innovation', 'Développement', 'Cloud', 'Sécurité'];

const BlogCard: React.FC<{ post: BlogPost; index: number }> = ({ post, index }) => {
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    const formattedDate = new Date(post.publishedAt).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    return (
        <motion.article
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden transition-transform duration-300 hover:scale-105"
        >
            <div className="relative h-48 w-full overflow-hidden">
                <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                />
                <div className="absolute top-4 right-4 bg-blue-600 text-white text-sm px-3 py-1 rounded-full">
                    {post.category}
                </div>
            </div>
            <div className="p-6">
                <div className="flex items-center mb-4">
                    <img
                        src={post.author.picture}
                        alt={post.author.name}
                        className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {post.author.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{formattedDate}</p>
                    </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    {post.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">{post.excerpt}</p>
                <a
                    href={`/blog/${post.slug}`}
                    className="text-blue-600 dark:text-blue-400 font-medium hover:text-blue-700 dark:hover:text-blue-300 transition-colors duration-200"
                >
                    Lire la suite →
                </a>
            </div>
        </motion.article>
    );
};

const BlogSection = () => {
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [selectedCategory, setSelectedCategory] = useState('Tous');
    const [loading, setLoading] = useState(true);
    const [ref, inView] = useInView({
        threshold: 0.1,
        triggerOnce: true,
    });

    useEffect(() => {
        // TODO: Integrate with Strapi/Sanity API
        // For now, using mock data
        const fetchPosts = async () => {
            try {
                // Simulate API call
                const mockPosts: BlogPost[] = [
                    {
                        id: '1',
                        title: 'L\'avenir de l\'IA en Haïti',
                        excerpt: 'Découvrez comment l\'intelligence artificielle transforme le paysage technologique haïtien...',
                        slug: 'avenir-ia-haiti',
                        coverImage: 'https://source.unsplash.com/random/800x600?ai',
                        author: {
                            name: 'Jean Robert',
                            picture: 'https://source.unsplash.com/random/100x100?face',
                        },
                        category: 'Innovation',
                        publishedAt: '2025-03-15',
                    },
                    {
                        id: '2',
                        title: 'Sécurité des Caméras IP : Guide Complet',
                        excerpt: 'Tout ce que vous devez savoir pour sécuriser vos caméras de surveillance IP contre les cyberattaques...',
                        slug: 'securite-cameras-ip',
                        coverImage: 'https://source.unsplash.com/random/800x600?security-camera',
                        author: {
                            name: 'Marie Claire',
                            picture: 'https://source.unsplash.com/random/100x100?woman',
                        },
                        category: 'Sécurité',
                        publishedAt: '2025-03-28',
                    },
                    {
                        id: '3',
                        title: 'Cloud Computing : Optimisez Vos Coûts',
                        excerpt: 'Stratégies et bonnes pratiques pour réduire vos dépenses cloud tout en maximisant la performance...',
                        slug: 'optimisation-couts-cloud',
                        coverImage: 'https://source.unsplash.com/random/800x600?cloud',
                        author: {
                            name: 'Pierre Michel',
                            picture: 'https://source.unsplash.com/random/100x100?man',
                        },
                        category: 'Cloud',
                        publishedAt: '2025-03-25',
                    },
                    {
                        id: '4',
                        title: 'React vs Vue : Choisir en 2025',
                        excerpt: 'Analyse comparative détaillée des deux frameworks les plus populaires pour le développement frontend...',
                        slug: 'react-vs-vue-2025',
                        coverImage: 'https://source.unsplash.com/random/800x600?coding',
                        author: {
                            name: 'Sophie Martin',
                            picture: 'https://source.unsplash.com/random/100x100?woman-tech',
                        },
                        category: 'Développement',
                        publishedAt: '2025-03-20',
                    },
                    {
                        id: '5',
                        title: 'IoT : Révolution Industrielle',
                        excerpt: 'Comment l\'Internet des Objets transforme l\'industrie et crée de nouvelles opportunités...',
                        slug: 'iot-revolution-industrielle',
                        coverImage: 'https://source.unsplash.com/random/800x600?iot',
                        author: {
                            name: 'Marc Antoine',
                            picture: 'https://source.unsplash.com/random/100x100?engineer',
                        },
                        category: 'Tech',
                        publishedAt: '2025-03-18',
                    },
                    {
                        id: '6',
                        title: '5G en Haïti : Perspectives',
                        excerpt: 'Les enjeux et opportunités du déploiement de la 5G pour le développement numérique...',
                        slug: '5g-haiti-perspectives',
                        coverImage: 'https://source.unsplash.com/random/800x600?5g',
                        author: {
                            name: 'Paul Henri',
                            picture: 'https://source.unsplash.com/random/100x100?telecom',
                        },
                        category: 'Innovation',
                        publishedAt: '2025-03-10',
                    },
                    {
                        id: '7',
                        title: 'Cyberdéfense : Tendances 2025',
                        excerpt: 'Les nouvelles menaces et solutions en cyberdéfense pour protéger votre entreprise...',
                        slug: 'cyberdefense-tendances-2025',
                        coverImage: 'https://source.unsplash.com/random/800x600?cybersecurity',
                        author: {
                            name: 'Luc Bernard',
                            picture: 'https://source.unsplash.com/random/100x100?security',
                        },
                        category: 'Sécurité',
                        publishedAt: '2025-03-05',
                    },
                    {
                        id: '8',
                        title: 'DevOps : Guide Pratique',
                        excerpt: 'Implémentez efficacement les pratiques DevOps dans votre équipe de développement...',
                        slug: 'devops-guide-pratique',
                        coverImage: 'https://source.unsplash.com/random/800x600?devops',
                        author: {
                            name: 'Anne Marie',
                            picture: 'https://source.unsplash.com/random/100x100?developer',
                        },
                        category: 'Développement',
                        publishedAt: '2025-03-01',
                    }
                ];
                setPosts(mockPosts);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching posts:', error);
                setLoading(false);
            }
        };

        fetchPosts();
    }, []);

    const filteredPosts = selectedCategory === 'Tous'
        ? posts
        : posts.filter(post => post.category === selectedCategory);

    return (
        <section className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                    ref={ref}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.5 }}
                    className="text-center mb-16"
                >
                    <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                        Notre Blog
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                        Restez informé des dernières tendances technologiques et découvrez nos analyses expertes
                    </p>
                </motion.div>

                {/* Categories Filter */}
                <div className="flex flex-wrap justify-center gap-2 mb-12">
                    {CATEGORIES.map((category) => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${selectedCategory === category
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-gray-600'
                                }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {filteredPosts.map((post, index) => (
                            <BlogCard key={post.id} post={post} index={index} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default BlogSection;